import React, { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  TextField,
  Typography,
} from "@mui/material";
import { UploadFileOutlined } from "@mui/icons-material";
import color from "../../../Constants/colors";
import { DocumentCraftingRevisionSummary } from "../../../Models/documentCrafting";
import { useUploadDocumentCraftingRevision } from "../../../Query/useDocumentCrafting";
import { getDocumentCraftingError } from "../../../Api/documentCrafting";
import { formatDate, formatFileSize } from "../Common/format";

/** Matches `revision_service.NOTE_MAX_LENGTH` in the backend. */
export const UPLOAD_NOTE_MAX = 2000;

interface Props {
  open: boolean;
  documentId: number;
  /** "docx" or "pptx": the only file type the document accepts. */
  nativeExt: string;
  /** The document's per-file limit (`max_upload_mb` of the document detail):
   * a personal document keeps the module limit whatever the viewer's
   * workspace sets. */
  maxUploadMb?: number | null;
  /** Saved revisions, newest first; the first is the default base. */
  revisions: DocumentCraftingRevisionSummary[];
  /** Preselected base revision (e.g. the one on screen). */
  defaultBaseId?: number;
  /** The caller has an open draft of the document: uploading discards it
   * (the server answers 409 `draft_exists` otherwise). */
  hasDraft?: boolean;
  /** Opens the caller's draft instead of uploading. */
  onOpenDraft?: () => void;
  onClose: () => void;
  /** The ingest run started. */
  onStarted: (runId: number) => void;
}

/** Validates a picked file against the document's native format and its
 * upload limit; returns the error key or null. */
export const uploadFileError = (
  file: File,
  nativeExt: string,
  maxUploadMb: number | null | undefined,
): "wrongType" | "tooLarge" | null => {
  if (!file.name.toLowerCase().endsWith(`.${nativeExt.toLowerCase()}`)) {
    return "wrongType";
  }
  if (maxUploadMb && file.size > maxUploadMb * 1024 * 1024) return "tooLarge";
  return null;
};

/**
 * Manual revision upload (SPEC §4.8): a DOCX/PPTX edited outside the app,
 * the base revision and an optional note. Processing runs in the background
 * and saves the next revision directly, so the run page opens next.
 */
const UploadRevisionDialog: React.FC<Props> = ({
  open,
  documentId,
  nativeExt,
  maxUploadMb,
  revisions,
  defaultBaseId,
  hasDraft = false,
  onOpenDraft,
  onClose,
  onStarted,
}) => {
  const { t, i18n } = useTranslation("documentCrafting");
  const input = useRef<HTMLInputElement>(null);
  const upload = useUploadDocumentCraftingRevision();
  const [file, setFile] = useState<File | null>(null);
  const [fileError, setFileError] = useState<"wrongType" | "tooLarge" | null>(
    null,
  );
  const [baseId, setBaseId] = useState<number | null>(null);
  const [note, setNote] = useState("");
  // The server reported a draft the page did not know about (409).
  const [draftConflict, setDraftConflict] = useState(false);

  const ext = nativeExt.toUpperCase();
  const base =
    baseId ??
    (defaultBaseId && revisions.some((revision) => revision.id === defaultBaseId)
      ? defaultBaseId
      : revisions[0]?.id);
  const noteTooLong = note.trim().length > UPLOAD_NOTE_MAX;
  const replacesDraft = hasDraft || draftConflict;

  const pick = (picked: File | undefined) => {
    if (!picked) return;
    const error = uploadFileError(picked, nativeExt, maxUploadMb);
    setFileError(error);
    setFile(error ? null : picked);
  };

  const submit = () => {
    if (!file || !base || noteTooLong) return;
    upload.mutate(
      {
        documentId,
        payload: {
          file,
          base_revision_id: base,
          note: note.trim(),
          // Manual upload has no draft step (SPEC §4.8): the user confirmed
          // that the open draft is replaced.
          ...(replacesDraft ? { discard_existing_draft: true } : {}),
        },
      },
      {
        onSuccess: (response) => onStarted(response.run_id),
        onError: (error) => {
          if (getDocumentCraftingError(error)?.code === "draft_exists") {
            setDraftConflict(true);
          }
        },
      },
    );
  };

  const reset = () => {
    setFile(null);
    setFileError(null);
    setBaseId(null);
    setNote("");
    setDraftConflict(false);
  };

  return (
    <Dialog
      open={open}
      onClose={upload.isPending ? undefined : onClose}
      maxWidth="sm"
      fullWidth
      TransitionProps={{ onExited: reset }}
    >
      <DialogTitle>{t("detail.upload.title")}</DialogTitle>
      <DialogContent>
        <Typography variant="body2" sx={{ mb: 2 }}>
          {t("detail.upload.text", { ext })}
        </Typography>

        <input
          ref={input}
          type="file"
          hidden
          accept={`.${nativeExt}`}
          data-testid="upload-revision-input"
          onChange={(event) => {
            pick(event.target.files?.[0]);
            event.target.value = "";
          }}
        />
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1.5,
            p: 1.5,
            borderRadius: "10px",
            border: `1px dashed ${fileError ? color.red600 : color.gray300}`,
            backgroundColor: color.gray50,
            flexWrap: "wrap",
          }}
        >
          <UploadFileOutlined sx={{ color: color.gray500 }} />
          <Box sx={{ flex: 1, minWidth: 0 }}>
            {file ? (
              <>
                <Typography
                  noWrap
                  sx={{ fontSize: 14, fontWeight: 600, color: color.gray900 }}
                >
                  {file.name}
                </Typography>
                <Typography sx={{ fontSize: 12, color: color.gray500 }}>
                  {formatFileSize(file.size, i18n.language)}
                </Typography>
              </>
            ) : (
              <Typography sx={{ fontSize: 13, color: color.gray500 }}>
                {maxUploadMb
                  ? t("detail.upload.limits", { ext, mb: maxUploadMb })
                  : t("detail.upload.limitsNoSize", { ext })}
              </Typography>
            )}
          </Box>
          <Button
            size="small"
            variant="outlined"
            color="secondary"
            onClick={() => input.current?.click()}
            disabled={upload.isPending}
          >
            {file
              ? t("detail.upload.change")
              : t("detail.upload.choose", { ext })}
          </Button>
        </Box>
        {fileError && (
          <Typography sx={{ mt: 0.75, fontSize: 12, color: color.red600 }}>
            {fileError === "wrongType"
              ? t("detail.upload.wrongType", { ext })
              : t("detail.upload.tooLarge", { mb: maxUploadMb })}
          </Typography>
        )}

        <TextField
          select
          fullWidth
          size="small"
          label={t("detail.upload.base")}
          value={base ?? ""}
          onChange={(event) => setBaseId(Number(event.target.value))}
          disabled={upload.isPending}
          sx={{ mt: 2.5 }}
        >
          {revisions.map((revision) => (
            <MenuItem key={revision.id} value={revision.id}>
              {t("detail.selector.revision", {
                number: revision.number,
                date: formatDate(revision.saved_at, i18n.language),
              })}
            </MenuItem>
          ))}
        </TextField>

        <TextField
          fullWidth
          multiline
          minRows={3}
          label={t("detail.upload.note")}
          placeholder={t("detail.upload.notePlaceholder")}
          value={note}
          onChange={(event) => setNote(event.target.value)}
          error={noteTooLong}
          helperText={`${note.trim().length} / ${UPLOAD_NOTE_MAX}`}
          disabled={upload.isPending}
          sx={{ mt: 2 }}
        />

        <Alert severity="info" sx={{ mt: 2 }}>
          {t("detail.upload.warning")}
        </Alert>
        {replacesDraft && (
          <Alert
            severity="warning"
            sx={{ mt: 2 }}
            data-testid="upload-revision-draft-warning"
            action={
              onOpenDraft ? (
                <Button
                  color="inherit"
                  size="small"
                  onClick={onOpenDraft}
                  disabled={upload.isPending}
                >
                  {t("detail.upload.openDraft")}
                </Button>
              ) : undefined
            }
          >
            {t("detail.upload.draftExists")}
          </Alert>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button color="secondary" onClick={onClose} disabled={upload.isPending}>
          {t("common.cancel")}
        </Button>
        <Button
          variant="contained"
          onClick={submit}
          disabled={!file || !base || noteTooLong || upload.isPending}
        >
          {replacesDraft
            ? t("detail.upload.submitDiscardingDraft")
            : t("detail.upload.submit")}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default UploadRevisionDialog;
